import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import MessageDialog from 'views/Components/CommonCustomComponents/messageDialog/MessageNotification';
import { checkPermissionGet } from 'utils/API/Merchant APIs/MerchantApiCalls';
import { globalNotifySuccess } from 'views/utilities/AlertToast';

/**
 * Generic Route Guard Component
 *
 * This component restricts access to routes based on configurable conditions.
 * It checks localStorage flags and redirects users to specific routes when conditions are met.
 *
 * Usage:
 * <RouteGuard restrictions={[
 *   { flag: 'kycRestricted', allowedPath: '/video-kyc/customer-management' },
 *   { flag: 'profileIncomplete', allowedPath: '/complete-profile' }
 * ]}>
 *   <YourComponent />
 * </RouteGuard>
 *
 * @param {Object} props
 * @param {Array} props.restrictions - Array of restriction configurations
 * @param {string} props.restrictions[].flag - localStorage key to check (value should be 'true' to activate)
 * @param {string} props.restrictions[].allowedPath - Path to redirect to when restricted
 * @param {number} props.restrictions[].priority - Optional priority (higher number = higher priority, default: 0)
 * @param {ReactNode} props.children - Child components to render
 */
const RouteGuard = ({ children, restrictions = [] }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [dialogConfig, setDialogConfig] = useState(null);
    const [checkLoading, setCheckLoading] = useState(false);

    useEffect(() => {
        const currentPath = location.pathname;

        // Sort restrictions by priority (highest first)
        const sortedRestrictions = [...restrictions].sort((a, b) => (b.priority || 0) - (a.priority || 0));

        let activeRestriction = null;

        // Check each restriction in priority order
        for (const restriction of sortedRestrictions) {
            const { flag, allowedPath } = restriction;
            const isRestricted = localStorage.getItem(flag) === 'true';

            if (isRestricted) {
                // If this restriction is active and user is not on the allowed path, redirect
                if (currentPath !== allowedPath) {
                    navigate(allowedPath, { replace: true });
                    return; // Stop checking other restrictions once we redirect
                }

                // If restricted but on allowed path, check if we need to show a dialog
                if (restriction.dialog) {
                    activeRestriction = restriction;
                    // Don't return here, we might need to process higher priority redirects if we weren't on allowed path
                    // But since we are on allowed path, we just look for dialogs
                    break;
                }
            }
        }

        if (activeRestriction && activeRestriction.dialog) {
            setDialogConfig(activeRestriction.dialog);
        } else {
            setDialogConfig(null);
        }
    }, [location.pathname, navigate, restrictions]);

    const handleDialogAction = async () => {
        if (dialogConfig?.onAgreeAction === 'CHECK_KYC_STATUS') {
            setCheckLoading(true);
            try {
                const response = await checkPermissionGet();
                if (response?.data?.status) {
                    const pixlKycStatus = response?.data?.pixlKycStatus;
                    const pixlKycVerificationStatus = response?.data?.pixlKycVerificationStatus;

                    localStorage.setItem('pixlKycStatus', pixlKycStatus || '');
                    localStorage.setItem('pixlKycVerificationStatus', pixlKycVerificationStatus || '');

                    if (pixlKycStatus === 'Verified' && pixlKycVerificationStatus === 'pending') {
                        // Still pending, maybe show toast
                        globalNotifySuccess('Status is still pending verification.');
                    } else {
                        // Status changed!
                        localStorage.setItem('kycPending', 'false');

                        // Re-evaluate restrictions
                        const anyEmpty = !pixlKycStatus || !pixlKycVerificationStatus;
                        const anyRejected = pixlKycStatus === 'Rejected' || pixlKycVerificationStatus === 'Rejected';
                        const kycRestricted = anyEmpty || anyRejected;
                        localStorage.setItem('kycRestricted', kycRestricted.toString());

                        globalNotifySuccess('Verification status updated!');
                        setDialogConfig(null);
                        // Force refresh or navigation might be needed if state depends on it?
                        // RouteGuard effect will re-run on render, but we might need to force re-render/navigate
                        window.location.reload();
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setCheckLoading(false);
            }
        }
    };

    return (
        <>
            {children}
            {dialogConfig && (
                <MessageDialog
                    open={true}
                    title={dialogConfig.title}
                    message={dialogConfig.message}
                    disableBackdropClick={dialogConfig.unclosable}
                    showCloseButton={!dialogConfig.unclosable}
                    onClose={() => setDialogConfig(null)}
                    agreeText={dialogConfig.agreeText}
                    onAgree={handleDialogAction}
                    agreeLoading={checkLoading}
                    showActions={!!dialogConfig.agreeText}
                />
            )}
        </>
    );
};

RouteGuard.propTypes = {
    children: PropTypes.node,
    restrictions: PropTypes.arrayOf(
        PropTypes.shape({
            flag: PropTypes.string.isRequired,
            allowedPath: PropTypes.string.isRequired,
            priority: PropTypes.number,
            dialog: PropTypes.shape({
                title: PropTypes.string,
                message: PropTypes.string,
                unclosable: PropTypes.bool
            })
        })
    )
};

export default RouteGuard;
